import { useCallback, useEffect } from 'react';
import styled from 'styled-components';

import type { FetchItemSummariesByCatalogIdQuery } from '@/redux/modules/item/summary/itemSummary.api';

import usePubNubCatalog from '@/components/LiveEventSubscribers/hooks/usePubNubCatalog';
import MessageScroller from '@/pages/BiddingConsole/components/MessageScroller/MessageScroller';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getCatalogAbsenteeBidders,
    getCatalogHasVideo,
    getCatalogIsCatalogOnly,
    getCatalogStartTime,
    getCatalogStatus,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { fetchCatalogItemSummaries } from '@/redux/modules/catalog/items/catalogItems.actions';
import { getLiveCatalogBidders, getLiveCatalogCurrentItemId, getLiveCatalogStatus } from '@/redux/modules/console';
import { fetchItemSummaryIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import {
    getItemSummariesForCatalog,
    getItemSummaryIndex,
    getItemSummaryPromoted,
    getItemSummarySellerId,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchCatalogPresence, fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { emptyItemSummary } from '@/types/item/ItemSummary';
import { ItemCardSource, useTrackItemCardClicked } from '@/utils/amplitude/analytics';
import { shouldShowLive } from '@/utils/live';
import TEST_HOUSES from '@/utils/testHouses';

import ConsoleSocialBadge from '../ConsoleSocial/ConsoleSocialBadge';
import CurrentItemLink from './components/CurrentItemLink/CurrentItemLink';
import ProgressBar from './components/ProgressBar/ProgressBar';
import UpcomingItemLink from './components/UpcomingItemLink/UpcomingItemLink';

type Props = {
    catalogId: number;
    className?: string;
};

const CatalogPageConsole = ({ catalogId, className }: Props) => {
    const dispatch = useAppDispatch();
    const trackItemCardClicked = useTrackItemCardClicked();

    const bidders = useAppSelector((state) => getLiveCatalogBidders(state, catalogId));
    const currentItem = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const liveCatalogStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const saleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const absenteeBidders = useAppSelector((state) => getCatalogAbsenteeBidders(state, catalogId));
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const hasVideo = useAppSelector((state) => getCatalogHasVideo(state, catalogId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));

    const items = useAppSelector((state) => getItemSummariesForCatalog(state, catalogId));

    const itemId = currentItem || items?.[0]?.itemId || 0;
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const itemIndex = index || 0;
    const viewers = bidders + absenteeBidders || 1;

    const nextItem = items.find((i) => i.index === itemIndex + 1) || emptyItemSummary;
    const thirdItem = items.find((i) => i.index === itemIndex + 2) || emptyItemSummary;

    const showLive = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);

    // Subscribe to PubNub presence updates for real-time bidder count
    // this also fixes the issue where the viewer count is different between console and this catalog page
    usePubNubCatalog({
        catalogId,
        subscribeToPresence: showLive,
    });

    useEffect(() => {
        if (showLive) {
            dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId));
            dispatch(fetchCatalogPresence(catalogId));
        }
    }, [catalogId, dispatch, showLive, sellerId]);

    useEffect(() => {
        // we will load the data for the items in the console
        if (showLive && itemId > 0) {
            const query: FetchItemSummariesByCatalogIdQuery = { index, pageSize: 4 };
            if (TEST_HOUSES.includes(sellerId)) {
                query.preview = true;
            }
            // fetch the currently active item card model
            dispatch(
                fetchItemSummaryIfNeeded({
                    identifier: 'catalog-console',
                    itemId,
                })
            );
            // fetch the next few
            dispatch(fetchCatalogItemSummaries({ catalogId, query }));
        }
    }, [catalogId, index, dispatch, showLive, sellerId, itemId]);

    const auctionPaused = liveCatalogStatus === 'paused';

    const handleItemClick = useCallback(
        (clickedItemId: number) => {
            trackItemCardClicked({
                catalogId,
                itemId: clickedItemId,
                promoted,
                source: ItemCardSource.CatalogPageConsole,
            });
        },
        [catalogId, promoted, trackItemCardClicked]
    );

    return (
        <ConsoleContainer className={className}>
            <ImagesContainer>
                <LiveItem
                    onClick={() => {
                        handleItemClick(itemId);
                    }}
                >
                    <CurrentItemLink
                        catalogId={catalogId}
                        itemId={itemId}
                        showLive={showLive}
                    />
                    <ConsoleSocialBadge
                        className="absolute left-xs top-xs"
                        hasVideo={hasVideo}
                        pause={auctionPaused}
                        viewers={viewers}
                    />
                    <MessageScrollerContainer>
                        <StyledMessageScroller
                            catalogId={catalogId}
                            convert
                            hideMessages
                            smallConsole
                        />
                    </MessageScrollerContainer>
                </LiveItem>
                {nextItem.itemId > 0 && (
                    <UpcomingItemLink
                        itemId={nextItem.itemId}
                        onClick={() => {
                            handleItemClick(nextItem.itemId);
                        }}
                    />
                )}
                {thirdItem.itemId > 0 && (
                    <UpcomingItemLink
                        itemId={thirdItem.itemId}
                        lastItem
                        onClick={() => {
                            handleItemClick(thirdItem.itemId);
                        }}
                    />
                )}
            </ImagesContainer>
            <ProgressBar catalogId={catalogId} />
        </ConsoleContainer>
    );
};

export default CatalogPageConsole;

const ConsoleContainer = styled.div`
    border-bottom: none;
    display: block;
    flex: 1 1 394px;
    max-width: 100%;
    width: 100%;
    margin: 0 auto;
`;

const ImagesContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    position: relative;
    height: 384px;
    width: 100%;
    margin-bottom: 10px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        justify-content: space-evenly;
    }
`;

const LiveItem = styled.div`
    height: 384px;
    width: calc(100% / 3);
    padding: 25px;
    position: relative;
    justify-content: center;
    display: flex;
    align-items: center;
    flex-direction: column;
    z-index: 4;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        width: 100%;
        padding: 0;
        border: none;
    }
`;

const MessageScrollerContainer = styled.div`
    display: block;
    z-index: 2;
    position: absolute;
    bottom: 4px;
    left: 0;
    width: 500px;
`;

const StyledMessageScroller = styled(MessageScroller)`
    margin-left: 0;
    z-index: 10;
`;
